import Layout from './Layout'
import { interpolate, renderRich, useTranslation } from '@/lib/i18n'

export interface BusinessIdentity {
  legalName: string
  tradingAs: string
  address: string
  jurisdiction: string
  supportEmail: string
  effectiveDate: string
}

// Blocks mirror the YAML structure under frontend.legal.<page>.sections —
// an ordered mix of paragraphs and lists so whole legal docs live in locales.
export interface LegalBlock {
  type: 'p' | 'ul'
  text?: string
  items?: string[]
}

export interface LegalSection {
  heading: string
  blocks: LegalBlock[]
}

interface LegalPageProps {
  page: 'terms' | 'returns' | 'privacy' | 'delivery'
  business: BusinessIdentity
}

/**
 * v2 · Template 06 — Legal, kept quiet.
 *
 * Single column, 64ch measure, 48px between sections. H1 28/700
 * sentence case, section H2 16/700 sentence case, body 15/1.6
 * ink-muted, hairline separators, ink-faint for dates and the operator
 * line. No accent except links.
 *
 * The entire document — headings, paragraphs, lists — is read from
 * config/locales, with %{business} placeholders interpolated here and
 * [links](/path) + **bold** rendered by renderRich.
 */
export default function LegalPage({ page, business }: LegalPageProps) {
  const { t, tx } = useTranslation()
  const sections = tx<LegalSection[]>(`legal.${page}.sections`) ?? []

  const vars = {
    legalName: business.legalName,
    tradingAs: business.tradingAs,
    jurisdiction: business.jurisdiction,
    supportEmail: business.supportEmail,
  }

  const rich = (text: string) => renderRich(interpolate(text, vars))

  return (
    <Layout showBack>
      <article className="mx-auto w-full max-w-[720px] px-8 pt-16 max-[559px]:px-4 max-[559px]:pt-10">
        <header className="border-b border-hairline pb-5">
          <div className="mb-2.5 text-[13px] text-ink-faint">
            {t(`legal.${page}.eyebrow`)}
          </div>
          <h1 className="text-[28px] font-bold leading-[1.15] tracking-[-0.02em] max-[559px]:text-[24px]">
            {t(`legal.${page}.title`)}
          </h1>
          <div className="mt-3 text-[13px] text-ink-faint">
            {t('legal.common.effective', { date: business.effectiveDate })}
          </div>
        </header>

        {sections.map((section) => (
          <section key={section.heading} className="mt-12">
            <h2 className="mb-3 text-[16px] font-bold">{section.heading}</h2>
            {section.blocks.map((block, index) =>
              block.type === 'ul' ? (
                <ul
                  key={index}
                  className="mb-3 max-w-[64ch] list-disc pl-5 text-[15px] leading-[1.6] text-ink-muted marker:text-ink-faint"
                >
                  {(block.items ?? []).map((item, itemIndex) => (
                    <li key={itemIndex} className="mb-1.5">
                      {rich(item)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p
                  key={index}
                  className="mb-3 max-w-[64ch] text-[15px] leading-[1.6] text-ink-muted"
                >
                  {rich(block.text ?? '')}
                </p>
              )
            )}
          </section>
        ))}

        <footer className="mt-12 border-t border-hairline pt-6">
          <div className="mb-2 text-[13px] text-ink-faint">
            {t('legal.common.operator')}
          </div>
          <p className="max-w-[64ch] text-[15px] leading-[1.6] text-ink-muted">
            {business.legalName}
            <br />
            {t('legal.common.trading_as')} {business.tradingAs}
            <br />
            {business.address}
            <br />
            {business.jurisdiction}
          </p>
          <p className="mt-4 max-w-[64ch] text-[15px] leading-[1.6] text-ink-muted">
            {rich(t('legal.common.questions'))}
          </p>
        </footer>
      </article>
    </Layout>
  )
}
